import { NavigateFunction } from "react-router-dom";
import { Dispatcher } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { createMonitoringAPICall } from "../../../utils/createMonitoringAPICall";
import { AxiosError, AxiosResponse } from "axios";
import {
  getCachedVideoUrl,
  cacheVideoUrl,
  getVideoUrlCacheKey,
  generateVideoRequestId,
  isVideoRequestValid,
  tryBeginVideoFetch,
  setFetchInProgress,
  clearFetchInProgress,
} from "./videoUrlCache";

export const checkForRedirectAndFetchMP4 = (
  selectedRow: any,
  navigate: NavigateFunction,
  setMP4URL: Dispatcher<string> | ((url: string, sourceRow?: any) => void),
  setVideoClipLoading: Dispatcher<boolean>,
  setShowRefreshButton: Dispatcher<boolean>,
  useCache = false, // Only true when called from row selection, not initial load
  getCurrentSelectedRowKey?: () => string | null,
  setVideoProcessing?: Dispatcher<boolean>,
  useAsyncGeneration = true
) => {
  if (!selectedRow) {
    return;
  }

  const cacheKey = getVideoUrlCacheKey(selectedRow);
  const applyVideoUrl = (url: string) => {
    (setMP4URL as (url: string, sourceRow?: any) => void)(url, selectedRow);
  };

  // Check if a fetch is already in progress for this exact row
  // If so, skip this duplicate fetch - the existing one will handle it
  // IMPORTANT: This check must happen BEFORE generateVideoRequestId() to avoid
  // invalidating the in-flight request
  if (useCache && !tryBeginVideoFetch(cacheKey)) {
    return;
  }
  if (!useCache) {
    setFetchInProgress(cacheKey);
  }

  const AxiosUI = createMonitoringAPICall(``);

  // Generate a new request ID for this fetch operation.
  // This invalidates any pending requests for previously selected rows.
  // IMPORTANT: Only generate after passing the dedup check above
  const requestId = generateVideoRequestId();

  const retryFunction = () => {
    // Only retry if this request is still valid (user hasn't selected another row)
    if (isVideoRequestValid(requestId)) {
      checkForRedirectAndFetchMP4(
        selectedRow,
        navigate,
        setMP4URL,
        setVideoClipLoading,
        setShowRefreshButton,
        useCache,
        getCurrentSelectedRowKey,
        setVideoProcessing,
        useAsyncGeneration
      );
    }
  };

  const clipBaseUrl = `alert/clip/?approx_capture_timestamp=${selectedRow.approx_capture_timestamp}&window_id=${selectedRow.window_id}`;
  const pollBaseUrl = `alert/video/?approx_capture_timestamp=${selectedRow.approx_capture_timestamp}&window_id=${selectedRow.window_id}`;

  // Always clear URL + set loading immediately so we never keep showing a previous row's video
  applyVideoUrl("");
  setVideoClipLoading(true);
  setShowRefreshButton(false);
  if (setVideoProcessing) {
    setVideoProcessing(false);
  }

  // Only check cache if explicitly enabled (row re-selection, not initial load)
  if (useCache) {
    const cachedUrl = getCachedVideoUrl(cacheKey);
    if (cachedUrl) {
      setShowRefreshButton(false);
      setVideoClipLoading(false);
      applyVideoUrl(cachedUrl);
      clearFetchInProgress();
      return;
    }
  }

  const fetchClip = (shouldUseAsync: boolean) => {
    const clipUrl = shouldUseAsync
      ? `${clipBaseUrl}&async_generation=true`
      : clipBaseUrl;

    AxiosUI.get(clipUrl).then(
      (response: AxiosResponse) => {
        // CRITICAL: Check if request is still valid BEFORE any state updates
        // This prevents state updates on unmounted components
        if (!isVideoRequestValid(requestId)) {
          // Still cache the result if it's a valid URL
          if (typeof response.data === "string") {
            cacheVideoUrl(cacheKey, response.data);
          }
          clearFetchInProgress();
          return;
        }

        const responseData = response.data;
        if (
          responseData &&
          typeof responseData === "object" &&
          responseData.status === "processing"
        ) {
          if (setVideoProcessing) {
            setVideoProcessing(true);
          }
          setShowRefreshButton(false);
          applyVideoUrl("");

          if (shouldUseAsync) {
            setVideoClipLoading(true);
            const pollUrl = responseData.poll_url
              ? responseData.poll_url.replace(/^\/?monitoring-api\//, "")
              : pollBaseUrl;
            pollForVideo(pollUrl, 10);
          } else {
            // Can't poll without async - show processing message instead of spinner
            setVideoClipLoading(false);
            clearFetchInProgress();
          }
          return;
        }

        if (typeof responseData !== "string") {
          if (setVideoProcessing) {
            setVideoProcessing(false);
          }
          setShowRefreshButton(true);
          applyVideoUrl("");
          setVideoClipLoading(false);
          clearFetchInProgress();
          return;
        }

        // Cache the result
        cacheVideoUrl(cacheKey, responseData);

        // Additional validation: Check if current selection still matches this request
        // But only if the ref has been set (has a valid key with actual data)
        if (getCurrentSelectedRowKey) {
          const currentSelectedRowKey = getCurrentSelectedRowKey();
          const expectedRowKey = getVideoUrlCacheKey(selectedRow);
          // Only reject if current key is valid (not empty) AND doesn't match
          // An empty key "|" means the ref hasn't been set yet (initial load)
          const isCurrentKeyValid =
            currentSelectedRowKey && currentSelectedRowKey !== "|";
          if (isCurrentKeyValid && currentSelectedRowKey !== expectedRowKey) {
            // Row changed but no new request was started - clear loading state
            // but don't set the URL (it's for the wrong row)
            if (setVideoProcessing) {
              setVideoProcessing(false);
            }
            setVideoClipLoading(false);
            clearFetchInProgress();
            return;
          }
        }

        if (setVideoProcessing) {
          setVideoProcessing(false);
        }
        setShowRefreshButton(false);
        applyVideoUrl(responseData);
        setVideoClipLoading(false);
        clearFetchInProgress();
      },
      (reason: AxiosError) => {
        // Only handle errors if this request is still valid
        if (!isVideoRequestValid(requestId)) {
          clearFetchInProgress();
          return;
        }

        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          // Check again before state updates (component may have unmounted)
          if (!isVideoRequestValid(requestId)) {
            clearFetchInProgress();
            return;
          }
          if (setVideoProcessing) {
            setVideoProcessing(false);
          }
          setShowRefreshButton(true);
          setTimeout(() => {
            // Final check before updating loading state
            if (isVideoRequestValid(requestId)) {
              setVideoClipLoading(false);
            }
            clearFetchInProgress();
          }, 2000);
        });
      }
    );
  };

  const pollForVideo = (pollUrl: string, retriesRemaining: number) => {
    // Don't cancel early - let the poll complete and cache the result
    // so it's ready if user clicks back to this alert
    setTimeout(() => {
      AxiosUI.get(pollUrl).then(
        (response: AxiosResponse) => {
          if (typeof response.data === "string") {
            // Always cache the result so it's available for quick access later
            cacheVideoUrl(cacheKey, response.data);

            // Only update UI if this request is still active
            if (!isVideoRequestValid(requestId)) {
              // Another request has taken over - it will handle loading state
              clearFetchInProgress();
              return;
            }
            if (getCurrentSelectedRowKey) {
              const currentSelectedRowKey = getCurrentSelectedRowKey();
              const expectedRowKey = getVideoUrlCacheKey(selectedRow);
              // Only reject if current key is valid (not empty) AND doesn't match
              const isCurrentKeyValid =
                currentSelectedRowKey && currentSelectedRowKey !== "|";
              if (
                isCurrentKeyValid &&
                currentSelectedRowKey !== expectedRowKey
              ) {
                // Row changed but no new request - clear loading but don't set URL
                if (setVideoProcessing) {
                  setVideoProcessing(false);
                }
                setVideoClipLoading(false);
                clearFetchInProgress();
                return;
              }
            }

            if (setVideoProcessing) {
              setVideoProcessing(false);
            }
            setShowRefreshButton(false);
            applyVideoUrl(response.data);
            setVideoClipLoading(false);
            clearFetchInProgress();
            return;
          }

          // Only update UI if still active
          if (!isVideoRequestValid(requestId)) {
            // Another request has taken over
            clearFetchInProgress();
            return;
          }
          if (setVideoProcessing) {
            setVideoProcessing(false);
          }
          setShowRefreshButton(true);
          setVideoClipLoading(false);
          clearFetchInProgress();
        },
        (error: AxiosError) => {
          const status = error.response?.status;
          if (status === 404) {
            if (retriesRemaining > 1) {
              pollForVideo(pollUrl, retriesRemaining - 1);
              return;
            }
            // Only fallback to sync if still active
            if (!isVideoRequestValid(requestId)) {
              // Another request has taken over - it will handle loading state
              clearFetchInProgress();
              return;
            }
            setVideoClipLoading(true);
            fetchClip(false);
            return;
          }

          // Only handle other errors if still active
          if (!isVideoRequestValid(requestId)) {
            // Another request has taken over
            clearFetchInProgress();
            return;
          }
          universalAPIErrorHandler(error, navigate, retryFunction, () => {
            // Check again before state updates (component may have unmounted)
            if (!isVideoRequestValid(requestId)) {
              clearFetchInProgress();
              return;
            }
            if (setVideoProcessing) {
              setVideoProcessing(false);
            }
            setShowRefreshButton(true);
            setVideoClipLoading(false);
            clearFetchInProgress();
          });
        }
      );
    }, 500); // Poll every 500ms for faster video display
  };

  fetchClip(useAsyncGeneration);
};
